import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download } from 'lucide-react';

interface Visitor {
  firstName: string;
  email: string;
  timestamp: string;
  userAgent: string;
}

export default function Admin() {
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [key, setKey] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    if (isAuthorized) {
      const storedVisitors = JSON.parse(localStorage.getItem('visitors') || '[]');
      setVisitors(storedVisitors);
    }
  }, [isAuthorized]);

  const handleAuth = () => {
    if (key === 'lyfy-admin-key-2024') {
      setIsAuthorized(true);
    }
  };

  const downloadData = () => {
    const data = visitors.map(visitor => 
      `Name: ${visitor.firstName}\nEmail: ${visitor.email}\nTimestamp: ${visitor.timestamp}\nUser Agent: ${visitor.userAgent}\n\n`
    ).join('---\n');

    const blob = new Blob([data], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visitor-data-${new Date().toISOString()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-md p-6 max-w-md w-full"
        >
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Admin Access</h1>
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
            placeholder="Enter admin key"
          />
          <button
            onClick={handleAuth}
            className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700"
          >
            Access Admin Panel
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Visitor Data</h1>
          <button
            onClick={downloadData}
            className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
          >
            <Download className="w-5 h-5" />
            <span>Download Data</span>
          </button>
        </div>

        <div className="grid gap-4">
          {visitors.map((visitor, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium">{visitor.firstName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{visitor.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Timestamp</p>
                  <p className="font-medium">{new Date(visitor.timestamp).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">User Agent</p>
                  <p className="font-medium text-sm">{visitor.userAgent}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
} 